import {
  ISubmissionWorkflowState,
  allPhases,
  endStates
} from './workflow-phase-ownership';

export const MY_SUBMISSIONS_TABS = {
  DRAFTS: 'drafts',
  SUBMITTED: 'submitted',
  REVISIONS: 'revisions',
  UNSUBMITTED: 'unsubmitted',
  COMPLETED: 'completed'
} as const;

export type MySubmissionsTab =
  (typeof MY_SUBMISSIONS_TABS)[keyof typeof MY_SUBMISSIONS_TABS];

export type MySubmissionsActiveTab = Exclude<
  MySubmissionsTab,
  typeof MY_SUBMISSIONS_TABS.DRAFTS
>;

export const REVISION_WORKFLOW_PHASES: ISubmissionWorkflowState[] = [
  'revision',
  'immediateRevision'
];

export const UNSUBMITTED_WORKFLOW_PHASES: ISubmissionWorkflowState[] = [
  'unsubmitted'
];

// `endStates` is a readonly tuple, so copy it into a mutable array.
export const COMPLETED_WORKFLOW_PHASES: ISubmissionWorkflowState[] = [
  ...endStates
];

export const NON_SUBMITTED_WORKFLOW_PHASES: ISubmissionWorkflowState[] = [
  ...REVISION_WORKFLOW_PHASES,
  ...UNSUBMITTED_WORKFLOW_PHASES,
  ...COMPLETED_WORKFLOW_PHASES
];

// Derived rather than hand-listed so the tabs stay exhaustive as phases are
// added, and so the tab counts can never drift from the lists they summarise.
export const SUBMITTED_WORKFLOW_PHASES: ISubmissionWorkflowState[] =
  allPhases.filter(phase => !NON_SUBMITTED_WORKFLOW_PHASES.includes(phase));

export const MY_SUBMISSIONS_TAB_PHASES: Record<
  MySubmissionsActiveTab,
  ISubmissionWorkflowState[]
> = {
  submitted: SUBMITTED_WORKFLOW_PHASES,
  revisions: REVISION_WORKFLOW_PHASES,
  unsubmitted: UNSUBMITTED_WORKFLOW_PHASES,
  completed: COMPLETED_WORKFLOW_PHASES
};

export type IMySubmissionsTotals = Record<MySubmissionsTab, number>;

/**
 * Totals per tab from a count of submissions per workflow phase, so that a
 * single aggregation can serve every tab.
 */
export const toMySubmissionsTotals = ({
  phaseCounts,
  draftTotal
}: {
  phaseCounts: Record<string, number>;
  draftTotal: number;
}): IMySubmissionsTotals => {
  const totals = {
    [MY_SUBMISSIONS_TABS.DRAFTS]: draftTotal
  } as IMySubmissionsTotals;

  for (const tab of Object.keys(
    MY_SUBMISSIONS_TAB_PHASES
  ) as MySubmissionsActiveTab[]) {
    totals[tab] = MY_SUBMISSIONS_TAB_PHASES[tab].reduce(
      (total, phase) => total + (phaseCounts[phase] ?? 0),
      0
    );
  }

  return totals;
};
